import { CSSProperties, FC, Fragment, ReactNode, useMemo, useRef, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbChevronRight, TbDatabaseSearch, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useListColumnIcons } from '@/hooks/useListColumnIcons';
import { useListViewColumns } from '@/hooks/useListViewColumns';
import { useMaps } from '@/hooks/useMaps';
import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import {
    DropdownMenuLabel,
    DropdownNestedHeader,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    LearnMoreFooter,
} from '@/shared';
import { useListStore, useListViewColumnStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccount, ConnectedAccountType, ListType, ListView, ListViewColumnDataType } from '@/types';

interface ListColumnOption {
    avatarStyling?: CSSProperties;
    connectedAccountId?: string;
    dataType: ListViewColumnDataType;
    icon?: ReactNode;
    key: string;
    label: string;
    src?: string;
}

interface ListColumnSection {
    icon?: ReactNode;
    key: string;
    label: string;
    options: ListColumnOption[];
}

interface AddColumnToListViewDropdownProps {
    listView?: ListView;
    children: ReactNode;
}

const AddColumnToListViewDropdown: FC<AddColumnToListViewDropdownProps> = ({ listView, children }): JSX.Element => {
    const [input, setInput] = useState('');
    const [open, setOpen] = useState(false);
    const [selectedDataSourceId, setSelectedDataSourceId] = useState<string>();
    const nestedNavigationInProgress = useRef(false);

    const { selectedList } = useListStore();
    const { listViewColumns } = useListViewColumnStore();
    const { createListViewColumn } = useListViewColumnStore();

    const { listColumnOptionsMap } = useListViewColumns();
    const { integrationLabelToIconMap } = useMaps();
    const { getColumnAvatar } = useListColumnIcons();
    const listViewAccess = useNestedResourceAccess('listViews', listView?.id);

    const existingColumnKeys = useMemo(() => new Set(listViewColumns?.map((col) => col.key)), [listViewColumns]);

    const sections = (listColumnOptionsMap?.[selectedList?.type as ListType] ?? []) as ListColumnSection[];
    const normalizedInput = input.trim().toLowerCase();
    const optionMatchesInput = (option: ListColumnOption) =>
        !normalizedInput ||
        [option.label, option.dataType].some((value) =>
            String(value ?? '')
                .toLowerCase()
                .includes(normalizedInput),
        );
    const nonDataSourceSections = sections.filter((section) => section.key !== 'dataSources');
    const dataSourceSection = sections.find((section) => section.key === 'dataSources');
    const filteredSections = nonDataSourceSections
        .map((section) => ({
            ...section,
            options: section.options.filter(optionMatchesInput),
        }))
        .filter((section) => section.options.length);
    const connectedDataSources = Array.from(
        new Map(
            (selectedList?.dataSources ?? [])
                .map((dataSource) => dataSource.connectedAccount)
                .filter((account): account is ConnectedAccount => Boolean(account?.id))
                .map((account) => [account.id, account]),
        ).values(),
    );
    const columnsForDataSource = (accountId: string) =>
        (dataSourceSection?.options ?? []).filter((option) => option.connectedAccountId === accountId);
    const filteredDataSources = connectedDataSources.filter((account) => {
        if (!normalizedInput) {
            return true;
        }

        return (
            [account.nickname, account.type].some((value) =>
                String(value ?? '')
                    .toLowerCase()
                    .includes(normalizedInput),
            ) || columnsForDataSource(account.id).some(optionMatchesInput)
        );
    });
    const selectedDataSource = connectedDataSources.find((account) => account.id === selectedDataSourceId);
    const selectedDataSourceColumns = selectedDataSource
        ? columnsForDataSource(selectedDataSource.id).filter(optionMatchesInput)
        : [];

    const { token } = useAntDesignTheme();

    const handleColumnSelect = (option: ListColumnOption) => {
        if (!listView || !listViewAccess.canUpdate || existingColumnKeys.has(option.key)) {
            return;
        }

        createListViewColumn({
            view: listView,
            key: option.key,
            label: option.label,
            dataType: option.dataType,
            isHidden: false,
            order: listViewColumns?.length ?? 0,
        });
    };

    const renderColumnOption = (option: ListColumnOption) => {
        const columnAdded = existingColumnKeys.has(option.key);

        return (
            <Menu.Item
                disabled={columnAdded || listViewAccess.update.disabled}
                key={option.key}
                onClick={() => handleColumnSelect(option)}
            >
                <DropdownOptionContent
                    avatar={getColumnAvatar(option)}
                    metadata={[option.dataType, columnAdded ? 'Column added' : null]}
                    selected={columnAdded}
                    title={option.label}
                />
            </Menu.Item>
        );
    };

    return (
        <Dropdown
            disabled={listViewAccess.update.disabled}
            onOpenChange={(nextOpen) => {
                if (!nextOpen && nestedNavigationInProgress.current) {
                    return;
                }

                setOpen(nextOpen);

                if (!nextOpen) {
                    setInput('');
                    setSelectedDataSourceId(undefined);
                }
            }}
            open={open}
            placement='bottomLeft'
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='Columns' variant='dropdown' />}
                    header={
                        selectedDataSource ? (
                            <DropdownNestedHeader
                                avatar={{
                                    icon: (
                                        <img
                                            alt={selectedDataSource.type ?? 'Data Source'}
                                            src={
                                                integrationLabelToIconMap[
                                                    selectedDataSource.type as ConnectedAccountType
                                                ]
                                            }
                                            style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                        />
                                    ),
                                }}
                                backLabel='Back to Data Sources'
                                description={selectedDataSource.type}
                                onBack={() => {
                                    setInput('');
                                    setSelectedDataSourceId(undefined);
                                }}
                                title={selectedDataSource.nickname ?? selectedDataSource.type ?? 'Data Source'}
                            />
                        ) : undefined
                    }
                    maxHeight={16 * 24}
                    search={{
                        placeholder: selectedDataSource ? 'Search Data Source Columns...' : 'Search Columns...',
                        setValue: setInput,
                        value: input,
                    }}
                    style={{ marginTop: '0.75rem' }}
                    width={16 * 20}
                >
                    {selectedDataSource ? (
                        selectedDataSourceColumns.length ? (
                            <DropdownPanelMenu selectable={false}>
                                {selectedDataSourceColumns.map(renderColumnOption)}
                            </DropdownPanelMenu>
                        ) : (
                            <EmptySection
                                height={16 * 12}
                                icon={<TbSearch size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Matching Data Source Columns'
                                description={
                                    input ? (
                                        <>
                                            No columns match <strong>{input}</strong>.
                                        </>
                                    ) : (
                                        'No columns are available for this data source.'
                                    )
                                }
                            />
                        )
                    ) : filteredSections.length || filteredDataSources.length ? (
                        <>
                            {filteredSections.map((section) => (
                                <Fragment key={section.key}>
                                    <DropdownMenuLabel icon={section.icon}>{section.label}</DropdownMenuLabel>
                                    <DropdownPanelMenu selectable={false}>
                                        {section.options.map(renderColumnOption)}
                                    </DropdownPanelMenu>
                                </Fragment>
                            ))}
                            {filteredDataSources.length ? (
                                <>
                                    <DropdownMenuLabel
                                        icon={dataSourceSection?.icon ?? <TbDatabaseSearch size='0.875rem' />}
                                    >
                                        Data Sources
                                    </DropdownMenuLabel>
                                    <DropdownPanelMenu selectable={false}>
                                        {filteredDataSources.map((account) => {
                                            const availableColumns = columnsForDataSource(account.id);
                                            const unaddedColumnCount = availableColumns.filter(
                                                (option) => !existingColumnKeys.has(option.key),
                                            ).length;

                                            return (
                                                <Menu.Item
                                                    key={account.id}
                                                    onClick={({ domEvent }) => {
                                                        domEvent.stopPropagation();
                                                        nestedNavigationInProgress.current = true;
                                                        window.setTimeout(() => {
                                                            nestedNavigationInProgress.current = false;
                                                        }, 0);
                                                        setInput('');
                                                        setSelectedDataSourceId(account.id);
                                                    }}
                                                >
                                                    <DropdownOptionContent
                                                        avatar={{
                                                            icon: (
                                                                <img
                                                                    alt={account.type ?? 'Data Source'}
                                                                    src={
                                                                        integrationLabelToIconMap[
                                                                            account.type as ConnectedAccountType
                                                                        ]
                                                                    }
                                                                    style={{
                                                                        ...imgLogoStyling,
                                                                        transform: 'scale(0.75)',
                                                                    }}
                                                                />
                                                            ),
                                                        }}
                                                        description={`${unaddedColumnCount} Available Column${
                                                            unaddedColumnCount === 1 ? '' : 's'
                                                        }`}
                                                        extra={
                                                            <TbChevronRight
                                                                size='1rem'
                                                                style={{
                                                                    color: token.colorTextTertiary,
                                                                    display: 'block',
                                                                }}
                                                            />
                                                        }
                                                        title={account.nickname ?? account.type ?? 'Data Source'}
                                                    />
                                                </Menu.Item>
                                            );
                                        })}
                                    </DropdownPanelMenu>
                                </>
                            ) : null}
                        </>
                    ) : (
                        <EmptySection
                            height={16 * 12}
                            icon={<TbSearch size='75%' style={{ color: token.colorTextTertiary }} />}
                            label='No Matching Columns'
                            description={
                                input ? (
                                    <>
                                        No columns match <strong>{input}</strong>.
                                    </>
                                ) : (
                                    'No columns are available for this list type.'
                                )
                            }
                        />
                    )}
                </DropdownPanel>
            )}
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default AddColumnToListViewDropdown;
